import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
  AreaChart, Area
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Minus, DollarSign, ShoppingCart, 
  Package, Users, RefreshCw, Calendar, BarChart3, MapPin,
  Clock, Store, Heart, Activity, Globe, Download, FileText
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

/**
 * AdminAnalytics - Platform-wide analytics dashboard for admins
 * 
 * Features:
 * - Platform summary metrics
 * - Demand heatmap by category
 * - User distribution by region
 * - Top performing retailers
 * - Time pattern analysis
 */
export default function AdminAnalytics() {
  const [timeframe, setTimeframe] = useState('weekly');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [regions, setRegions] = useState(null);
  const [topRetailers, setTopRetailers] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, [timeframe]);

  const exportCSV = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/analytics/admin/export/csv?timeframe=${timeframe}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `platform_analytics_${timeframe}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/analytics/admin/export/pdf?timeframe=${timeframe}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `platform_analytics_${timeframe}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [summaryRes, heatmapRes, regionsRes, retailersRes] = await Promise.all([
        api.get(`/analytics/admin/summary?timeframe=${timeframe}`),
        api.get(`/analytics/admin/demand-heatmap?timeframe=${timeframe}`),
        api.get('/analytics/admin/users-by-region'),
        api.get(`/analytics/admin/top-retailers?timeframe=${timeframe}`)
      ]);
      
      setSummary(summaryRes.data);
      setHeatmap(heatmapRes.data);
      setRegions(regionsRes.data);
      setTopRetailers(retailersRes.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (direction) => {
    if (direction === 'up') return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (direction === 'down') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const formatChartData = (chartData) => {
    if (!chartData?.labels) return [];
    return chartData.labels.map((label, idx) => ({
      name: label,
      revenue: chartData.datasets?.[0]?.data?.[idx] || 0,
      orders: chartData.datasets?.[1]?.data?.[idx] || 0
    }));
  };

  const formatCategoryData = (data) => {
    if (!data?.labels) return [];
    return data.labels.map((label, idx) => ({
      name: label,
      value: data.values?.[idx] || 0,
      revenue: data.revenue?.[idx] || 0
    }));
  };

  const formatHourData = (data) => {
    if (!data?.labels) return [];
    return data.labels.map((hour, idx) => ({
      hour: `${hour}:00`,
      orders: data.values?.[idx] || 0
    }));
  };

  const formatDayData = (data) => {
    if (!data?.labels) return [];
    return data.labels.map((day, idx) => ({
      day: day.substring(0, 3),
      orders: data.values?.[idx] || 0
    }));
  };

  if (loading && !summary) {
    return (
      <div className="space-y-6 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-indigo-600" />
            Platform Analytics
          </h2>
          <p className="text-gray-500 text-sm">Monitor platform-wide performance</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeframe} onValueChange={setTimeframe}>
            <SelectTrigger className="w-32" data-testid="admin-analytics-timeframe">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadAnalytics} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={exportCSV} 
            disabled={exporting || loading}
            data-testid="admin-export-csv-btn"
          >
            <Download className={`w-4 h-4 mr-2 ${exporting ? 'animate-bounce' : ''}`} />
            CSV
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={exportPDF} 
            disabled={exporting || loading}
            data-testid="admin-export-pdf-btn"
          >
            <FileText className={`w-4 h-4 mr-2 ${exporting ? 'animate-bounce' : ''}`} />
            PDF
          </Button>
        </div>
      </div>

      {/* Platform Summary Cards */}
      {summary && (
        <div className="grid md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-green-500" data-testid="admin-revenue-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Revenue</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${summary.summary.total_revenue.toLocaleString()}
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-lg">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <div className="flex items-center gap-1 mt-2 text-sm">
                {getTrendIcon(summary.trends.trend_direction)}
                <span className={summary.trends.revenue_change_percent >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {summary.trends.revenue_change_percent >= 0 ? '+' : ''}{summary.trends.revenue_change_percent}%
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500" data-testid="admin-orders-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Orders</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.summary.total_orders}</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg">
                  <ShoppingCart className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <p className="text-sm text-gray-400 mt-2">
                Avg: ${summary.summary.average_order_value}
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500" data-testid="admin-users-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Users</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {summary.users.total_consumers + summary.users.total_retailers}
                  </p>
                </div>
                <div className="bg-purple-100 p-3 rounded-lg">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <div className="flex gap-2 mt-2 text-xs">
                <Badge variant="outline">{summary.users.total_consumers} consumers</Badge>
                <Badge variant="outline">{summary.users.total_retailers} retailers</Badge>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-pink-500" data-testid="admin-charity-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Charity Donations</p>
                  <p className="text-2xl font-bold text-pink-600">
                    ${summary.summary.total_charity_donations.toLocaleString()}
                  </p>
                </div>
                <div className="bg-pink-100 p-3 rounded-lg">
                  <Heart className="w-6 h-6 text-pink-600" />
                </div>
              </div>
              <p className="text-sm text-gray-400 mt-2">
                ${summary.summary.total_consumer_savings.toLocaleString()} consumer savings
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* New Users Summary */}
      {summary?.users && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Growth This Period
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">+{summary.users.new_consumers_period}</p>
                <p className="text-sm text-gray-600">New Consumers</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-3xl font-bold text-blue-600">+{summary.users.new_retailers_period}</p>
                <p className="text-sm text-gray-600">New Retailers</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-3xl font-bold text-purple-600">{summary.users.active_retailers}</p>
                <p className="text-sm text-gray-600">Active Retailers</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <Tabs defaultValue="trends" className="space-y-4">
        <TabsList>
          <TabsTrigger value="trends" data-testid="admin-tab-trends">
            <BarChart3 className="w-4 h-4 mr-2" />
            Trends
          </TabsTrigger>
          <TabsTrigger value="demand" data-testid="admin-tab-demand">
            <Package className="w-4 h-4 mr-2" />
            Demand
          </TabsTrigger>
          <TabsTrigger value="time" data-testid="admin-tab-time">
            <Clock className="w-4 h-4 mr-2" />
            Time Patterns
          </TabsTrigger>
          <TabsTrigger value="retailers" data-testid="admin-tab-retailers">
            <Store className="w-4 h-4 mr-2" />
            Top Retailers
          </TabsTrigger>
          <TabsTrigger value="regions" data-testid="admin-tab-regions">
            <Globe className="w-4 h-4 mr-2" />
            Regions
          </TabsTrigger>
        </TabsList>

        {/* Trends Tab */}
        <TabsContent value="trends">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Platform Revenue & Orders</CardTitle>
            </CardHeader>
            <CardContent>
              {summary?.chart_data && (
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={formatChartData(summary.chart_data)}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#10b981" 
                      fillOpacity={1}
                      fill="url(#colorRevenue)"
                      strokeWidth={2}
                      name="Revenue ($)"
                    />
                    <Bar dataKey="orders" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Orders" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Demand Heatmap Tab */}
        <TabsContent value="demand">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Category Demand</CardTitle>
              </CardHeader>
              <CardContent>
                {heatmap?.category_heatmap && (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart 
                      data={formatCategoryData(heatmap.category_heatmap).slice(0, 8)} 
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                      <Bar dataKey="value" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Items Sold" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Retailer Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {heatmap?.retailer_distribution && (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={heatmap.retailer_distribution.labels.map((label, idx) => ({
                          name: label.length > 15 ? label.substring(0, 15) + '...' : label,
                          value: heatmap.retailer_distribution.values[idx]
                        }))}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="value"
                        label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      >
                        {heatmap.retailer_distribution.labels.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Insights */}
          {heatmap?.insights && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-lg">Key Insights</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-purple-50 rounded-lg text-center">
                    <p className="text-sm text-gray-600">Top Category</p>
                    <p className="text-xl font-bold text-purple-600">{heatmap.insights.top_category || 'N/A'}</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <p className="text-sm text-gray-600">Peak Day</p>
                    <p className="text-xl font-bold text-blue-600">{heatmap.insights.peak_day || 'N/A'}</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <p className="text-sm text-gray-600">Peak Hour</p>
                    <p className="text-xl font-bold text-green-600">
                      {heatmap.insights.peak_hour !== null ? `${heatmap.insights.peak_hour}:00` : 'N/A'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Time Patterns Tab */}
        <TabsContent value="time">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Orders by Hour</CardTitle>
              </CardHeader>
              <CardContent>
                {heatmap?.time_patterns?.by_hour && (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={formatHourData(heatmap.time_patterns.by_hour)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={2} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                      <Bar dataKey="orders" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Orders by Day of Week</CardTitle>
              </CardHeader>
              <CardContent>
                {heatmap?.time_patterns?.by_day && (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={formatDayData(heatmap.time_patterns.by_day)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                      <Bar dataKey="orders" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Top Retailers Tab */}
        <TabsContent value="retailers">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Top Performing Retailers</CardTitle>
            </CardHeader>
            <CardContent>
              {topRetailers?.retailers && topRetailers.retailers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Rank</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Retailer</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-600">Revenue</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-600">Orders</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topRetailers.retailers.map((retailer, idx) => (
                        <tr key={retailer.drlp_id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <Badge className={
                              idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                              idx === 1 ? 'bg-gray-100 text-gray-700' :
                              idx === 2 ? 'bg-orange-100 text-orange-700' :
                              'bg-blue-100 text-blue-700'
                            }>
                              #{idx + 1}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 font-medium">{retailer.drlp_name}</td>
                          <td className="text-right py-3 px-4 text-green-600 font-medium">
                            ${retailer.revenue.toLocaleString()}
                          </td>
                          <td className="text-right py-3 px-4">{retailer.orders}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No retailer data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Regions Tab */}
        <TabsContent value="regions">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  Consumers by Region
                </CardTitle>
              </CardHeader>
              <CardContent>
                {regions?.consumers_by_region && regions.consumers_by_region.length > 0 ? (
                  <div className="space-y-3">
                    {regions.consumers_by_region.slice(0, 5).map((region, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-blue-500" />
                          <span className="text-sm font-medium">
                            Region {region.lat.toFixed(0)}°, {region.lng.toFixed(0)}°
                          </span>
                        </div>
                        <Badge variant="outline">{region.count} users</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No region data available</p>
                )}
                <p className="text-sm text-gray-400 mt-4 text-center">
                  Total: {regions?.total_consumers || 0} consumers
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Store className="w-5 h-5 text-green-500" />
                  Retailers by Region
                </CardTitle>
              </CardHeader>
              <CardContent>
                {regions?.retailers_by_region && regions.retailers_by_region.length > 0 ? (
                  <div className="space-y-3">
                    {regions.retailers_by_region.slice(0, 5).map((region, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-green-500" />
                          <span className="text-sm font-medium">
                            Region {region.lat.toFixed(0)}°, {region.lng.toFixed(0)}°
                          </span>
                        </div>
                        <Badge variant="outline">{region.count} stores</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No region data available</p>
                )}
                <p className="text-sm text-gray-400 mt-4 text-center">
                  Total: {regions?.total_retailers || 0} retailers
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
